/**
 * Module-level source of truth for the active locale — the twin of
 * `src/lib/theme/themeStore.ts`, and for the same reason: the logout teardown
 * and the post-login hydration must be able to set it from outside React, so
 * account B never renders a single frame in account A's language.
 *
 * Persistence stays on `veltric.locale`, read by `pickInitialLocale()` before
 * any auth state exists.
 */

import { removePref, setPref, subscribePref } from "@/lib/storage/userPrefs";
import { SUPPORTED_LOCALES, pickInitialLocale } from "@/lib/i18n/locales";

export const LOCALE_KEY = "veltric.locale";

let current: string = pickInitialLocale();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

export function getLocale(): string {
  return current;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `persist: false` is the cross-tab echo path — adopt what another tab chose
 * without writing it back (see the same guard in themeStore).
 */
export function setLocale(next: string, opts?: { persist?: boolean }): void {
  if (!SUPPORTED_LOCALES.includes(next)) return;
  if (opts?.persist !== false) setPref(LOCALE_KEY, next);
  if (current === next) return;
  current = next;
  emit();
}

/**
 * Logout: drop the account's language so the login screen falls back to the
 * browser's own language via `pickInitialLocale()`. Unlike the theme, this key
 * was previously never cleared at all — account A's language stayed with
 * account B, permanently if B had no `languageCode` of its own.
 */
export function resetLocaleToDefault(): void {
  removePref(LOCALE_KEY);
  const next = pickInitialLocale();
  if (current === next) return;
  current = next;
  emit();
}

subscribePref(LOCALE_KEY, (value) => {
  // A logout in another tab removes the key; fall back the same way we do here.
  if (value === null) {
    const next = pickInitialLocale();
    if (current === next) return;
    current = next;
    emit();
    return;
  }
  setLocale(value, { persist: false });
});

export { SUPPORTED_LOCALES };
